import CryptoJS from 'crypto-js';
import { useParams } from 'react-router-dom';
import environment from '../environment';

const SECRET =
  process.env.REACT_APP_ENCRYPTION_SECRET ||
  process.env.VITE_ENCRYPTION_SECRET ||
  environment?.encryptionSecret ||
  '682e36bcd5fb57ace4ea85cf027fb01b8a2e2f32782a9efb4ab7c3cbdf47fd13';

// 32-byte key derived via SHA-256 (matches backend)
const KEY = CryptoJS.SHA256(SECRET);

/**
 * Encrypt request payload
 * Returns: "<iv_hex>:<ciphertext_base64>"
 */
export function encryptPayload(data) {
  try {
    const iv = CryptoJS.lib.WordArray.random(16);
    const jsonStr = typeof data === 'string' ? data : JSON.stringify(data);

    const encrypted = CryptoJS.AES.encrypt(jsonStr, KEY, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return `${iv.toString(CryptoJS.enc.Hex)}:${encrypted.toString()}`;
  } catch (err) {
    console.error('[cryptoHelper] Encryption error');
    throw err;
  }
}

/**
 * Decrypt response payload
 */
export function decryptPayload(encryptedStr) {
  try {
    if (!encryptedStr || typeof encryptedStr !== 'string' || !encryptedStr.includes(':')) {
      return encryptedStr;
    }

    const [ivHex, ciphertextBase64] = encryptedStr.split(':');
    const iv = CryptoJS.enc.Hex.parse(ivHex);

    const decrypted = CryptoJS.AES.decrypt(ciphertextBase64, KEY, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    let decryptedStr = '';
    try {
      decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);
    } catch {
      return encryptedStr;
    }

    if (!decryptedStr) return encryptedStr;

    try {
      return JSON.parse(decryptedStr);
    } catch {
      return decryptedStr;
    }
  } catch {
    return encryptedStr;
  }
}

/**
 * Check if a route param value is already encrypted
 * Format: "enc_<32-hex-iv>_<urlsafe-base64-ciphertext>"
 */
export function isEncryptedRouteParam(str) {
  if (typeof str !== 'string') return false;
  return /^enc_[0-9a-fA-F]{32}_[A-Za-z0-9_-]+$/.test(str);
}

/**
 * Encrypt a route parameter value for safe URL usage
 */
export function encryptRouteParam(val) {
  if (val === null || val === undefined || val === '') return val;
  const str = String(val);
  if (isEncryptedRouteParam(str)) return str;

  try {
    const iv = CryptoJS.lib.WordArray.random(16);
    const encrypted = CryptoJS.AES.encrypt(str, KEY, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    const ciphertextBase64 = encrypted.toString();
    const urlSafeCiphertext = ciphertextBase64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');

    const ivHex = iv.toString(CryptoJS.enc.Hex);
    return `enc_${ivHex}_${urlSafeCiphertext}`;
  } catch (err) {
    console.error('[cryptoHelper] Route param encryption error:', err);
    return val;
  }
}

/**
 * Decrypt a route parameter value back to plaintext
 */
export function decryptRouteParam(val) {
  if (!val || typeof val !== 'string') return val;
  if (!isEncryptedRouteParam(val)) {
    return val;
  }

  try {
    const withoutPrefix = val.slice(4); // Remove "enc_"
    const sepIdx = withoutPrefix.indexOf('_');
    if (sepIdx === -1) return val;

    const ivHex = withoutPrefix.slice(0, sepIdx);
    const urlSafeCiphertext = withoutPrefix.slice(sepIdx + 1);

    let base64 = urlSafeCiphertext
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    const iv = CryptoJS.enc.Hex.parse(ivHex);
    const decrypted = CryptoJS.AES.decrypt(base64, KEY, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    let decryptedStr = '';
    try {
      decryptedStr = decrypted.toString(CryptoJS.enc.Utf8);
    } catch {
      return val;
    }
    if (!decryptedStr) return val;
    return decryptedStr;
  } catch {
    return val;
  }
}

/**
 * Helper to encrypt parameterized IDs in paths
 * Matches patterns like /<resource>/(detail|edit)/<id>
 */
export function encryptPath(to) {
  if (!to) return to;
  if (typeof to === 'object') {
    if (to.pathname) {
      return {
        ...to,
        pathname: encryptPathString(to.pathname),
      };
    }
    return to;
  }
  if (typeof to === 'string') {
    return encryptPathString(to);
  }
  return to;
}

function encryptPathString(pathStr) {
  if (!pathStr || typeof pathStr !== 'string') return pathStr;
  return pathStr.replace(
    /(\/(?:detail|edit)\/)([^/?#]+)/g,
    (match, prefix, id) => {
      if (!id || isEncryptedRouteParam(id)) return match;
      return `${prefix}${encryptRouteParam(id)}`;
    }
  );
}

/**
 * Helper to decrypt parameterized IDs in paths
 */
export function decryptPath(to) {
  if (!to) return to;
  if (typeof to === 'object') {
    if (to.pathname) {
      return {
        ...to,
        pathname: decryptPathString(to.pathname),
      };
    }
    return to;
  }
  if (typeof to === 'string') {
    return decryptPathString(to);
  }
  return to;
}

function decryptPathString(pathStr) {
  if (!pathStr || typeof pathStr !== 'string') return pathStr;
  return pathStr.replace(
    /(\/(?:detail|edit)\/)([^/?#]+)/g,
    (match, prefix, id) => {
      if (!id || !isEncryptedRouteParam(id)) return match;
      return `${prefix}${decryptRouteParam(id)}`;
    }
  );
}

/**
 * Custom hook to get decrypted route params from useParams()
 */
export function useDecryptedParams() {
  const params = useParams();
  const decrypted = {};
  for (const [key, val] of Object.entries(params)) {
    decrypted[key] = val ? decryptRouteParam(val) : val;
  }
  return decrypted;
}

const cryptoHelper = {
  encryptPayload,
  decryptPayload,
  isEncryptedRouteParam,
  encryptRouteParam,
  decryptRouteParam,
  encryptPath,
  decryptPath,
  useDecryptedParams,
};

export default cryptoHelper;
